/**
 * Compress an image File to a JPEG data URL, memory-efficient via createObjectURL.
 * Handles large phone photos (5-15MB HEIC/JPEG) reliably.
 * Returns the data URL on success, or null if the file isn't a decodable image.
 */
export async function compressImage(file, { maxDim = 2400, quality = 0.85 } = {}) {
  if (!file) return null;
  const type = (file.type || "").toLowerCase();
  // Skip non-images. HEIC arrives as image/heic or image/heif — canvas can't decode natively,
  // but modern iOS Safari auto-converts HEIC on file pick, so the incoming type is usually image/jpeg.
  if (!type.startsWith("image/")) return null;

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = (e) => reject(new Error("Image decode failed (unsupported format?)"));
      i.src = objectUrl;
    });

    const longest = Math.max(img.width, img.height);
    const scale = longest > maxDim ? maxDim / longest : 1;
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context unavailable");
    ctx.drawImage(img, 0, 0, w, h);

    // Fallback: if toDataURL throws (huge canvas on old iOS), catch and retry smaller
    try {
      return canvas.toDataURL("image/jpeg", quality);
    } catch (e) {
      canvas.width = Math.round(w * 0.6);
      canvas.height = Math.round(h * 0.6);
      const ctx2 = canvas.getContext("2d");
      ctx2.drawImage(img, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL("image/jpeg", 0.7);
    }
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
